import { useState, useRef, useEffect } from 'react';

const DEFAULT_EXAMPLE_PROMPTS = [
  'What does this panel show?',
  'Explain the trend in this data',
  'Why is there a spike?',
  'Add a threshold at 80%',
  'Change the legend to table mode',
];

export { DEFAULT_EXAMPLE_PROMPTS };

export function useAnimatedPlaceholder(staticPlaceholder: string, prompts: string[], hasUserInput: boolean): string {
  const [display, setDisplay] = useState('');
  const indexRef = useRef(0);
  const charRef = useRef(0);
  const phaseRef = useRef<'typing' | 'pause' | 'erasing'>('typing');

  useEffect(() => {
    indexRef.current = 0;
    charRef.current = 0;
    phaseRef.current = 'typing';
    setDisplay('');

    if (hasUserInput || prompts.length === 0) {
      return;
    }

    const tick = () => {
      const currentPrompt = prompts[indexRef.current];

      if (phaseRef.current === 'typing') {
        charRef.current++;
        setDisplay(currentPrompt.slice(0, charRef.current));
        if (charRef.current >= currentPrompt.length) {
          phaseRef.current = 'pause';
        }
      } else if (phaseRef.current === 'pause') {
        phaseRef.current = 'erasing';
      } else if (phaseRef.current === 'erasing') {
        charRef.current--;
        setDisplay(currentPrompt.slice(0, charRef.current));
        if (charRef.current <= 0) {
          phaseRef.current = 'typing';
          indexRef.current = (indexRef.current + 1) % prompts.length;
        }
      }
    };

    const getDelay = () => {
      if (phaseRef.current === 'pause') {
        return 800;
      }
      if (phaseRef.current === 'erasing') {
        return 15;
      }
      return 30 + Math.random() * 20;
    };

    let timeoutId: ReturnType<typeof setTimeout>;
    const schedule = () => {
      timeoutId = setTimeout(() => {
        tick();
        schedule();
      }, getDelay());
    };
    schedule();

    return () => clearTimeout(timeoutId);
  }, [hasUserInput, prompts]);

  if (hasUserInput || prompts.length === 0) {
    return staticPlaceholder;
  }

  return display;
}
